import React, {
  createContext,
  useContext,
  useState,
  type ReactNode,
} from "react";

export interface IAnswerState {
  selected_choice_id: string | null;
  excluded_choices: string[];
  marked_for_review: boolean;
}

interface ExamContextType {
  excludeMode: boolean;
  setExcludeMode: (value: boolean) => void;
  excludedChoices: { [questionId: string]: string[] };
  setExcludedChoices: (value: { [questionId: string]: string[] }) => void;
  answers: Record<string, IAnswerState>;
  setAnswers: (value: Record<string, IAnswerState>) => void;
  updateAnswer: (questionId: string, answer: Partial<IAnswerState>) => void;
  isReferenceModalOpen: boolean;
  setIsReferenceModalOpen: (value: boolean) => void;
}

const ExamContext = createContext<ExamContextType | undefined>(undefined);

interface ExamProviderProps {
  children: ReactNode;
}

export const ExamProvider: React.FC<ExamProviderProps> = ({ children }) => {
  const [excludeMode, setExcludeMode] = useState(false);
  const [excludedChoices, setExcludedChoices] = useState<{
    [questionId: string]: string[];
  }>({});
  const [answers, setAnswers] = useState<Record<string, IAnswerState>>({});
  const [isReferenceModalOpen, setIsReferenceModalOpen] = useState(false);

  const updateAnswer = (questionId: string, answer: Partial<IAnswerState>) => {
    setAnswers((prev) => ({
      ...prev,
      [questionId]: {
        ...prev[questionId],
        ...answer,
      },
    }));
  };

  return (
    <ExamContext.Provider
      value={{
        excludeMode,
        setExcludeMode,
        excludedChoices,
        setExcludedChoices,
        answers,
        setAnswers,
        updateAnswer,
        isReferenceModalOpen,
        setIsReferenceModalOpen,
      }}
    >
      {children}
    </ExamContext.Provider>
  );
};

export const useExamContext = () => {
  const context = useContext(ExamContext);
  if (context === undefined) {
    throw new Error("useExamContext must be used within an ExamProvider");
  }
  return context;
};
